import { ReactNode } from 'react';
import { useQuery, QueryClient, QueryClientProvider, QueryKey, Query } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { THttpBodyType, THttpMethod, fetchMethods } from '@/types/FetchService.types';
import { TAdvancedRoleSlug } from '@/features/roles/store/models/advancedRole.model';
import { canCallEndpoint } from '@/hooks/useAccessControl';
import { filterPCIDSSEnpoint } from '@/features/pcidss/helpers/filterPCIDSSEnpoint';
import { parseURL } from '@/utils/network/parseURL';

export const defaultReadStaleTime = 1000;
export const defaultWriteStaleTime = 300;

export type TReturnType = 'text' | 'json' | undefined;

interface IQueryOptions extends RequestInit {
  returnType?: TReturnType;
  staleTime?: number;
  userRole?: TAdvancedRoleSlug;
  body?: THttpBodyType;
}

export interface IKeyParameters<T> {
  method: THttpMethod;
  user?: number | string;
  action?: string;
  id?: number | string;
  params?: T;
}

export type TQueryKey<T> = [string, IKeyParameters<T>?];

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: defaultReadStaleTime } } });

export function getClient() {
  return queryClient;
}

export function QueryProvider({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      {children}

      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={true} />}
    </QueryClientProvider>
  );
}

async function fetchData(url: string, options: IQueryOptions) {
  const { returnType, staleTime, ...fetchOptions } = options || {};

  const response = await fetch(url, fetchOptions);
  if (returnType) {
    if (returnType === 'text') {
      const result = await response.text();
      return result;
    } else if (returnType === 'json') {
      const result = await response.json();
      return result;
    }
  }

  return response;
}

export async function useFetchQuery<T>(key: TQueryKey<T>, url: string, options?: IQueryOptions) {
  const queryState = useQuery({
    queryKey: key,
    queryFn: async () => fetchData(url, { method: key[1].method, ...options }),
  });
  return queryState;
}

export async function fetchQuery<T>(key: TQueryKey<T>, url: string, options?: IQueryOptions) {
  const parsedUrl = parseURL(url);

  const filteredUrl = filterPCIDSSEnpoint({
    ...parsedUrl,
    method: key[1].method,
    body: options.body,
  });

  const { hasAccess, error } = await canCallEndpoint(
    {
      method: filteredUrl.method,
      url: `${filteredUrl.baseURL.replace('///', '')}/${filteredUrl.pathname}${filteredUrl.search ? `?${filteredUrl.search}` : ''}`,
    },
    options?.userRole,
  );

  if (!hasAccess) {
    console.error(error);
    throw new Error(error);
  }

  let staleTime = options && options.staleTime !== undefined ? options.staleTime : defaultReadStaleTime;

  if (filteredUrl.method !== fetchMethods.GET) {
    staleTime = defaultWriteStaleTime;
  }

  const queryData = await queryClient.fetchQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await fetchData(
        `${filteredUrl.baseURL}/${filteredUrl.pathname}${filteredUrl.search ? `?${filteredUrl.search}` : ''}`,
        {
          method: key[1].method,
          ...options,
          body: filteredUrl.body,
        },
      );
      if (response.status === 204 || response.status === 201) return { response };
      const data = await response?.clone().json();
      return { response, data };
    },
    staleTime,
  });

  return queryData.response.clone();
}

interface IQueryFilters {
  isActive?: boolean;
  isCanceled?: boolean;
}

export function getQueries(filters?: IQueryFilters): Query[] {
  const cache = queryClient.getQueryCache();
  const queries = cache.findAll();
  if (filters.isCanceled) {
    return queries.filter((q) => q.meta && q.options.meta.canceled === true);
  }
  return queries;
}

export function getRunningQueries(): Query[] {
  const queries = getQueries({ isActive: true });
  return queries.filter((q) => q.state.status === 'pending');
}

export function cancelQuery(key: QueryKey) {
  const query = queryClient.getQueryCache().find({ queryKey: key });
  query.options.meta = {
    canceled: true,
  };
  queryClient.cancelQueries({ queryKey: key });
}
